type ChangeEvent = "*" | "INSERT" | "UPDATE" | "DELETE";

export interface PostgresChangePayload<T = Record<string, unknown>> {
  schema: string;
  table: string;
  commit_timestamp: string;
  eventType: "INSERT" | "UPDATE" | "DELETE";
  new: T;
  old: Partial<T>;
  errors: null;
}

export type ChannelStatus =
  | "SUBSCRIBED"
  | "CHANNEL_ERROR"
  | "TIMED_OUT"
  | "CLOSED";

interface Binding {
  type: "postgres_changes" | "broadcast" | "presence";
  filter: Record<string, unknown>;
  callback: (payload: never) => void;
  id?: number;
}

export interface ChannelOptions {
  config?: {
    broadcast?: { self?: boolean; ack?: boolean };
    presence?: { key?: string };
  };
}

/** A topic on the realtime socket: Postgres changes, broadcast messages and presence. */
export class RealtimeChannel {
  private readonly bindings: Binding[] = [];
  private presence: Record<string, Record<string, unknown>[]> = {};
  state: "closed" | "joining" | "joined" = "closed";
  private statusCallback:
    | ((status: ChannelStatus, err?: Error) => void)
    | null = null;

  constructor(
    readonly topic: string,
    private readonly options: ChannelOptions,
    private readonly socket: RealtimeClient,
  ) {}

  on(
    type: "postgres_changes",
    filter: {
      event: ChangeEvent;
      schema?: string;
      table?: string;
      filter?: string;
    },
    callback: (payload: PostgresChangePayload) => void,
  ): this;
  on(
    type: "broadcast",
    filter: { event: string },
    callback: (payload: {
      type: "broadcast";
      event: string;
      payload: unknown;
    }) => void,
  ): this;
  on(
    type: "presence",
    filter: { event: "sync" | "join" | "leave" },
    callback: (payload: {
      key?: string;
      newPresences?: unknown[];
      leftPresences?: unknown[];
    }) => void,
  ): this;
  on(
    type: Binding["type"],
    filter: Record<string, unknown>,
    callback: (payload: never) => void,
  ): this {
    this.bindings.push({ type, filter, callback });
    return this;
  }

  subscribe(callback?: (status: ChannelStatus, err?: Error) => void): this {
    this.statusCallback = callback ?? null;
    this.state = "joining";
    this.socket.join(this);
    return this;
  }

  /** @internal */
  joinPayload() {
    const changes = this.bindings.filter((b) => b.type === "postgres_changes");
    return {
      topic: this.topic,
      config: {
        broadcast: this.options.config?.broadcast ?? {},
        presence: this.options.config?.presence ?? {},
        postgres_changes: changes.map((b) => ({
          event: b.filter.event ?? "*",
          schema: b.filter.schema ?? "public",
          table: b.filter.table,
          filter: b.filter.filter,
        })),
      },
    };
  }

  /** @internal */
  onJoinReply(status: "ok" | "error", response: Record<string, unknown>) {
    if (this.state === "closed") return; // unsubscribed while the join was in flight
    if (status !== "ok") {
      this.state = "closed";
      this.statusCallback?.(
        "CHANNEL_ERROR",
        new Error(String(response.reason ?? "Subscription failed")),
      );
      return;
    }
    const ids =
      (response.postgres_changes as { id: number }[] | undefined) ?? [];
    const changes = this.bindings.filter((b) => b.type === "postgres_changes");
    changes.forEach((b, i) => (b.id = ids[i]?.id));
    this.state = "joined";
    this.statusCallback?.("SUBSCRIBED");
  }

  /** @internal */
  onMessage(msg: Record<string, unknown>) {
    if (msg.type === "postgres_changes") {
      const ids = (msg.ids as number[]) ?? [];
      for (const b of this.bindings)
        if (
          b.type === "postgres_changes" &&
          b.id !== undefined &&
          ids.includes(b.id)
        )
          (b.callback as (p: unknown) => void)(msg.data);
    } else if (msg.type === "broadcast") {
      for (const b of this.bindings) {
        if (
          b.type === "broadcast" &&
          (b.filter.event === "*" || b.filter.event === msg.event)
        )
          (b.callback as (p: unknown) => void)({
            type: "broadcast",
            event: msg.event,
            payload: msg.payload,
          });
      }
    } else if (msg.type === "presence_state") {
      this.presence =
        (msg.state as Record<string, Record<string, unknown>[]>) ?? {};
      this.emitPresence("sync", {});
    } else if (msg.type === "presence_diff") {
      const joins =
        (msg.joins as Record<string, Record<string, unknown>[]>) ?? {};
      const leaves =
        (msg.leaves as Record<string, Record<string, unknown>[]>) ?? {};
      for (const [key, metas] of Object.entries(joins)) {
        this.presence[key] = [...(this.presence[key] ?? []), ...metas];
        this.emitPresence("join", { key, newPresences: metas });
      }
      for (const [key, metas] of Object.entries(leaves)) {
        const refs = new Set(metas.map((m) => m.presence_ref));
        const left = (this.presence[key] ?? []).filter(
          (m) => !refs.has(m.presence_ref),
        );
        if (left.length) this.presence[key] = left;
        else delete this.presence[key];
        this.emitPresence("leave", { key, leftPresences: metas });
      }
      this.emitPresence("sync", {});
    } else if (msg.type === "system" && msg.status === "error") {
      this.statusCallback?.("CHANNEL_ERROR", new Error(String(msg.message)));
    }
  }

  private emitPresence(event: string, payload: Record<string, unknown>) {
    for (const b of this.bindings)
      if (b.type === "presence" && b.filter.event === event)
        (b.callback as (p: unknown) => void)(payload);
  }

  presenceState(): Record<string, Record<string, unknown>[]> {
    return this.presence;
  }

  async track(payload: Record<string, unknown>) {
    return this.socket.push({
      type: "presence",
      topic: this.topic,
      event: "track",
      payload,
    });
  }

  async untrack() {
    return this.socket.push({
      type: "presence",
      topic: this.topic,
      event: "untrack",
    });
  }

  /** Sends a broadcast to everyone on the topic (`type` must be 'broadcast'). */
  async send(message: {
    type: "broadcast";
    event: string;
    payload?: unknown;
  }): Promise<"ok" | "error"> {
    if (this.state !== "joined") return "error";
    const ack = this.options.config?.broadcast?.ack;
    const res = this.socket.push(
      {
        type: "broadcast",
        topic: this.topic,
        event: message.event,
        payload: message.payload ?? null,
      },
      Boolean(ack),
    );
    return ack ? res : "ok";
  }

  async unsubscribe() {
    // Detach synchronously, so channel(topic) right after this returns a fresh channel
    // (React StrictMode unmounts and remounts effects back to back).
    const wasOpen = this.state !== "closed";
    this.state = "closed";
    this.socket.remove(this);
    if (wasOpen) this.statusCallback?.("CLOSED");
    await this.socket.push({ type: "leave", topic: this.topic }, false);
    return "ok" as const;
  }
}

/** WebSocket connection to `/realtime/v1/websocket`; reconnects with backoff and rejoins channels. */
export class RealtimeClient {
  private ws: WebSocket | null = null;
  private readonly channels = new Map<string, RealtimeChannel>();
  private ref = 0;
  private readonly pending = new Map<
    string,
    (status: "ok" | "error", response: Record<string, unknown>) => void
  >();
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private reconnectAttempts = 0;
  private closedByUser = false;
  private accessToken: string | null = null;
  private opening: Promise<void> | null = null;

  constructor(
    private readonly url: string,
    private readonly apiKey: string,
    private readonly tokenSource: () => Promise<string>,
  ) {}

  channel(topic: string, options: ChannelOptions = {}) {
    const existing = this.channels.get(topic);
    if (existing) return existing;
    const ch = new RealtimeChannel(topic, options, this);
    this.channels.set(topic, ch);
    return ch;
  }

  getChannels() {
    return [...this.channels.values()];
  }

  /** @internal */
  remove(channel: RealtimeChannel) {
    if (this.channels.get(channel.topic) === channel) this.channels.delete(channel.topic);
    // Close the socket once nothing uses it, but not when a channel is re-created right away.
    if (this.channels.size === 0) {
      setTimeout(() => {
        if (this.channels.size === 0) this.disconnect();
      }, 0);
    }
  }

  removeChannel(channel: RealtimeChannel) {
    return channel.unsubscribe();
  }

  async removeAllChannels() {
    await Promise.all([...this.channels.values()].map((c) => c.unsubscribe()));
  }

  /** Re-authorizes the socket (call after sign-in / token refresh; createClient does this for you). */
  setAuth(token: string | null) {
    this.accessToken = token;
    if (token && this.ws?.readyState === 1)
      void this.push({ type: "access_token", token });
  }

  private connect(): Promise<void> {
    if (this.ws && this.ws.readyState === 1) return Promise.resolve();
    this.opening ??= (async () => {
      this.closedByUser = false;
      const token = this.accessToken ?? (await this.tokenSource());
      const wsUrl = `${this.url.replace(/^http/, "ws")}/websocket?apikey=${encodeURIComponent(this.apiKey)}${token && token !== this.apiKey ? `&access_token=${encodeURIComponent(token)}` : ""}`;
      await new Promise<void>((resolve, reject) => {
        const ws = new WebSocket(wsUrl);
        this.ws = ws;
        ws.onopen = () => {
          this.reconnectAttempts = 0;
          this.heartbeat = setInterval(
            () => void this.push({ type: "heartbeat" }),
            25_000,
          );
          resolve();
        };
        ws.onerror = () => reject(new Error("Realtime connection failed"));
        ws.onmessage = (ev) => this.onMessage(String(ev.data));
        ws.onclose = () => this.onClose();
      });
    })().finally(() => {
      this.opening = null;
    });
    return this.opening;
  }

  private onMessage(raw: string) {
    let msg: Record<string, unknown>;
    try {
      msg = JSON.parse(raw);
    } catch {
      return;
    }
    if (msg.type === "reply" && msg.ref !== undefined && msg.ref !== null) {
      const resolver = this.pending.get(String(msg.ref));
      if (resolver) {
        this.pending.delete(String(msg.ref));
        resolver(
          msg.status as "ok" | "error",
          (msg.response as Record<string, unknown>) ?? {},
        );
      }
      return;
    }
    const topic = msg.topic as string | undefined;
    if (topic) this.channels.get(topic)?.onMessage(msg);
  }

  private onClose() {
    if (this.heartbeat) clearInterval(this.heartbeat);
    this.ws = null;
    for (const resolve of this.pending.values())
      resolve("error", { reason: "Connection closed" });
    this.pending.clear();
    for (const ch of this.channels.values())
      if (ch.state === "joined") ch.state = "joining";
    if (this.closedByUser || this.channels.size === 0) return;
    const delay = Math.min(1000 * 2 ** this.reconnectAttempts++, 10_000);
    setTimeout(() => {
      void this.connect()
        .then(() => {
          for (const ch of this.channels.values()) this.join(ch);
        })
        .catch(() => this.onClose());
    }, delay);
  }

  /** @internal */
  join(channel: RealtimeChannel) {
    void this.connect()
      .then(async () => {
        const ref = String(++this.ref);
        const reply = new Promise<{
          status: "ok" | "error";
          response: Record<string, unknown>;
        }>((resolve) =>
          this.pending.set(ref, (status, response) =>
            resolve({ status, response }),
          ),
        );
        this.ws!.send(
          JSON.stringify({ type: "join", ref, ...channel.joinPayload() }),
        );
        const { status, response } = await reply;
        channel.onJoinReply(status, response);
      })
      .catch((err: Error) =>
        channel.onJoinReply("error", { reason: err.message }),
      );
  }

  /** @internal */
  async push(
    message: Record<string, unknown>,
    awaitReply = true,
  ): Promise<"ok" | "error"> {
    if (!this.ws || this.ws.readyState !== 1) return "error";
    const ref = String(++this.ref);
    if (!awaitReply) {
      this.ws.send(JSON.stringify({ ...message, ref }));
      return "ok";
    }
    const reply = new Promise<"ok" | "error">((resolve) => {
      this.pending.set(ref, (status) => resolve(status));
      setTimeout(() => {
        if (this.pending.delete(ref)) resolve("error");
      }, 10_000);
    });
    this.ws.send(JSON.stringify({ ...message, ref }));
    return reply;
  }

  disconnect() {
    this.closedByUser = true;
    if (this.heartbeat) clearInterval(this.heartbeat);
    this.ws?.close();
    this.ws = null;
  }
}
